import React, { useEffect, useState } from "react";
import { StyledLoader } from "./style";

// Global animation start time - shared across all preloader instances
const GLOBAL_ANIMATION_START = Date.now();
const ANIMATION_DURATION = 2400; // 2.4 seconds in milliseconds

// Helper function to calculate current animation delay
const calculateAnimationDelay = () => {
  const timeSinceStart = Date.now() - GLOBAL_ANIMATION_START;
  const currentCycleTime = timeSinceStart % ANIMATION_DURATION;
  const delay = -(currentCycleTime / 1000); // Convert to negative seconds
  return `${delay}s`;
};

const Preloader = React.memo(({ style }: { style?: React.CSSProperties }) => {
  // Calculate initial delay immediately to avoid static view
  const [animationDelay, setAnimationDelay] = useState<string>(calculateAnimationDelay());

  useEffect(() => {
    // Recalculate after a micro-task to ensure accuracy
    const timeout = setTimeout(() => {
      setAnimationDelay(calculateAnimationDelay());
    }, 0);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <StyledLoader style={style} $animationDelay={animationDelay}>
      <div className="animation-container">
        <svg id="HeartHandsLogo" className="logo-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 70">
          <defs>
            <path
              id="left-hand-path"
              d="M48.08,41.78c-.71-1.44-2.41-3.01-3.83-3.76-1.87-.98-3.85-1.7-5.84-2.41-2.57-.91-5.15-1.78-7.73-2.68-.84-.3-1.69-.61-2.46-1.04-.56-.32-1.08-.8-1.51-1.29-1.3-1.52-1.9-3.35-2.16-5.3-.22-1.67-.21-3.36-.06-5.05.12-1.3.54-2.46,1.28-3.51,1.24-1.75,2.76-3.24,4.37-4.65.74-.65,1.65-.93,2.62-.9,1.06.02,2.13.12,3.19.24.96.12,1.71.63,2.24,1.45.99,1.55,1.95,3.11,2.99,4.62.53.77,1.16,1.49,1.85,2.12,1.5,1.37,3.39.94,4.47-.66.24-.37.72-1.25.26-4.9-.2-1.58-.93-3.03-1.39-4.55-.5-1.61-1.49-2.82-2.95-3.68-3.62-2.13-7.47-3.78-11.32-5.44-1.29-.55-2.65-.51-3.89.14-1.14.59-2.23,1.28-3.3,2-2.71,1.83-5.38,3.73-7.9,5.83-1.79,1.49-3.59,2.98-5.15,4.72-1.18,1.3-2.02,2.76-2.26,4.51-.24,1.79-.46,3.59-.64,5.39-.27,2.59-.45,5.18-1.05,7.72-.71,2.94-1.67,5.77-3.56,8.19-1.09,1.41-2.26,2.77-3.42,4.11-.61.69-.93,1.45-.92,2.36,0,.62-.02,1.24.02,1.86.15,2.36.71,4.6,2,6.61,2.07,3.22,5.1,5.13,8.68,6.27.83.26,1.47.1,2.12-.44,1.48-1.21,2.98-2.39,4.49-3.55,3.42-2.59,6.79-5.24,10.38-7.6,4.23-2.79,8.65-3.56,13.39-1.41,1,.46,2.07.71,3.16.81,1.44.14,2.72-.18,3.61-1.44.09-.12.14-.26.22-.43.06.05,1.03-2.18,0-4.26Z"
            />
            <path
              id="right-hand-path"
              d="M.47,46.03c.08.17.13.31.22.43.89,1.26,2.17,1.58,3.61,1.44,1.09-.1,2.16-.35,3.16-.81,4.74-2.15,9.16-1.38,13.39,1.41,3.59,2.36,6.96,5.01,10.38,7.6,1.51,1.16,3.01,2.34,4.49,3.55.65.54,1.29.7,2.12.44,3.58-1.14,6.61-3.05,8.68-6.27,1.29-2.01,1.85-4.25,2-6.61.04-.62.02-1.24.02-1.86,0-.91-.31-1.67-.92-2.36-1.16-1.34-2.33-2.7-3.42-4.11-1.89-2.42-2.85-5.25-3.56-8.19-.6-2.54-.78-5.13-1.05-7.72-.18-1.8-.4-3.6-.64-5.39-.24-1.75-1.08-3.21-2.26-4.51-1.56-1.74-3.36-3.23-5.15-4.72-2.52-2.1-5.19-4-7.9-5.83-1.07-.72-2.16-1.41-3.3-2-1.24-.65-2.6-.69-3.89-.14-3.85,1.66-7.7,3.31-11.32,5.44-1.46.86-2.45,2.07-2.95,3.68-.46,1.52-1.19,2.97-1.39,4.55-.46,3.65.02,4.53.26,4.9,1.08,1.6,2.97,2.03,4.47.66.69-.63,1.32-1.35,1.85-2.12,1.04-1.51,2-3.07,2.99-4.62.53-.82,1.28-1.33,2.24-1.45,1.06-.12,2.13-.22,3.19-.24.97-.03,1.88.25,2.62.9,1.61,1.41,3.13,2.9,4.37,4.65.74,1.05,1.16,2.21,1.28,3.51.15,1.69.16,3.38-.06,5.05-.26,1.95-.86,3.78-2.16,5.3-.43.49-.95.97-1.51,1.29-.77.43-1.62.74-2.46,1.04-2.58.9-5.16,1.77-7.73,2.68-1.99.71-3.97,1.43-5.84,2.41-1.42.75-3.12,2.32-3.83,3.76-1.03,2.08-.06,4.31,0,4.26Z"
            />
          </defs>
          <g transform="translate(50, 5)">
            <g className="left-hand-animator">
              <g transform="translate(-48.05, 0)">
                <use href="#left-hand-path" fill="#ffd21d" />
              </g>
            </g>
            <g className="right-hand-animator">
              <g transform="translate(-0.5, 0)">
                <use href="#right-hand-path" fill="#ffd21d" />
              </g>
            </g>
          </g>
        </svg>
      </div>
    </StyledLoader>
  );
});

Preloader.displayName = "Preloader";

export default Preloader;
